import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Seo } from '../components/Seo';
import { HotelMap } from '../components/HotelMap';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { openDeal } from '../lib/deals';
import { formatPrice } from '../lib/currency';
import { fmtDateISO } from '../lib/dateFormat';
import { toast } from 'sonner';
import { Star, MapPin, Coffee, Wifi, ExternalLink, Share2, ChevronLeft, ShieldCheck } from 'lucide-react';

function todayPlus(d) { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); }

export default function HotelDetail() {
  const { hotelId } = useParams();
  const [params] = useSearchParams();
  const { currency, promptEsim } = useApp();
  const navigate = useNavigate();
  const [checkin, setCheckin] = useState(params.get('ci') || todayPlus(14));
  const [checkout, setCheckout] = useState(params.get('co') || todayPlus(17));
  const [guests, setGuests] = useState(Number(params.get('g')) || 2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  const nights = Math.max(1, Math.round((new Date(checkout) - new Date(checkin)) / 86400000));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.hotelDetail(hotelId, { checkin, checkout, nights, guests });
      setData(res);
    } catch (e) { setData(null); } finally { setLoading(false); }
  }, [hotelId, checkin, checkout, nights, guests]);

  useEffect(() => { load(); }, [load]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: data?.name, text: `Check out ${data?.name} on FlightDeals.in`, url });
      else { await navigator.clipboard.writeText(url); toast.success('Link copied — share this stay!'); }
    } catch (e) { /* cancelled */ }
  };

  const book = () => {
    openDeal({ type: 'hotel', offer: data, surface: 'hotel_detail', source: `/hotels/${hotelId}` });
    promptEsim({ country_code: data?.country_code, country: data?.country, city: data?.city });
  };

  const [similar, setSimilar] = useState([]);
  useEffect(() => {
    if (!data) return;
    const budget = data.price_inr ? Math.round(data.price_inr * 1.25) : 0;
    api.hotelsSimilar(hotelId, { checkin, checkout, nights, guests, budget, limit: 6 })
      .then(({ data: r }) => setSimilar(r.offers || [])).catch(() => setSimilar([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.hotel_id, checkin, checkout]);

  const ratingLabel = (s) => (s >= 9 ? 'Exceptional' : s >= 8 ? 'Excellent' : s >= 7 ? 'Very good' : s >= 6 ? 'Good' : s >= 5 ? 'Pleasant' : 'Rated');

  if (loading) return <Layout><div className="max-w-6xl mx-auto px-4 py-8 space-y-4"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-96 rounded-2xl" /></div></Layout>;
  if (!data) return <Layout><div className="max-w-6xl mx-auto px-4 py-16 text-center"><p className="font-semibold">Hotel not found</p><Link to="/hotels" className="text-accent text-sm mt-2 inline-block">← Back to hotels</Link></div></Layout>;

  const imgs = data.images && data.images.length ? data.images : [data.image].filter(Boolean);
  const hotelJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: data.name,
    url: `https://flightdeals.in/hotels/${hotelId}`,
    ...(imgs.length ? { image: imgs.slice(0, 5) } : {}),
    ...(data.overview ? { description: data.overview } : {}),
    address: {
      '@type': 'PostalAddress',
      ...(data.address ? { streetAddress: data.address } : {}),
      ...(data.city ? { addressLocality: data.city } : {}),
      ...(data.state ? { addressRegion: data.state } : {}),
      ...(data.zipcode ? { postalCode: data.zipcode } : {}),
      ...(data.country_code ? { addressCountry: data.country_code } : {}),
    },
    ...(data.lat != null && data.lng != null ? { geo: { '@type': 'GeoCoordinates', latitude: data.lat, longitude: data.lng } } : {}),
    ...(data.stars ? { starRating: { '@type': 'Rating', ratingValue: data.stars, bestRating: 5 } } : {}),
    ...(data.review_score ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: data.review_score, bestRating: 10, reviewCount: data.review_count || 0 } } : {}),
    ...(data.price_inr ? { priceRange: `₹${data.price_inr}` } : {}),
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://flightdeals.in/' },
      { '@type': 'ListItem', position: 2, name: 'Hotels', item: 'https://flightdeals.in/hotels' },
      ...(data.city ? [{ '@type': 'ListItem', position: 3, name: data.city, item: 'https://flightdeals.in/hotels' }] : []),
      { '@type': 'ListItem', position: data.city ? 4 : 3, name: data.name, item: `https://flightdeals.in/hotels/${hotelId}` },
    ],
  };

  return (
    <Layout>
      <Seo title={`${data.name} — ${data.city}`} path={`/hotels/${hotelId}`}
        description={(data.overview || `Book ${data.name} in ${data.city} on FlightDeals.in — photos, guest reviews, map and live prices.`).slice(0, 155)}
        image={imgs[0]} jsonLd={[hotelJsonLd, breadcrumbJsonLd]} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/hotels" className="text-sm text-muted-foreground hover:text-accent inline-flex items-center gap-1 mb-3" data-testid="hotel-detail-back"><ChevronLeft className="h-4 w-4" /> Back to hotels</Link>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-1">{Array.from({ length: data.stars || 0 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="hotel-detail-name">{data.name}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5" /> {[data.address, data.city, data.state, data.country].filter(Boolean).join(', ')}</p>
          </div>
          <div className="flex items-center gap-2">
            {data.review_score ? <div className="text-right"><span className="inline-flex rounded-lg bg-accent/12 text-accent px-2.5 py-1 text-base font-semibold">{data.review_score}</span><p className="text-[11px] text-muted-foreground mt-0.5">{(data.review_count || 0).toLocaleString('en-IN')} reviews</p></div> : null}
            <Button variant="outline" onClick={share} className="h-10 rounded-xl font-semibold" data-testid="hotel-detail-share"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
          </div>
        </div>

        {/* Gallery */}
        {imgs.length > 0 && (
          <div className="mt-4 grid gap-2" data-testid="hotel-detail-gallery">
            <img src={imgs[active]} alt={data.name} className="w-full h-[360px] object-cover rounded-2xl" />
            {imgs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {imgs.map((src, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`h-20 w-28 shrink-0 rounded-lg overflow-hidden border-2 ${i === active ? 'border-accent' : 'border-transparent'}`}>
                    <img src={src} alt={`${data.name} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1">{data.accommodation_type || 'Hotel'}</span>
              {data.room_type ? <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1">{data.room_type}</span> : null}
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1"><Coffee className="h-3.5 w-3.5" /> Breakfast: {data.include_breakfast ? 'included' : 'not included'}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1"><Wifi className="h-3.5 w-3.5" /> Wi-Fi: {data.free_wifi ? 'free' : 'paid'}</span>
            </div>
            {data.review_score ? (
              <div className="rounded-2xl border border-border p-4" data-testid="hotel-rating-breakdown">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-accent text-accent-foreground text-lg font-bold">{data.review_score}</span>
                  <div>
                    <p className="font-semibold">{ratingLabel(data.review_score)}</p>
                    <p className="text-xs text-muted-foreground">Based on {(data.review_count || 0).toLocaleString('en-IN')} verified guest reviews</p>
                  </div>
                </div>
                <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-accent rounded-full" style={{ width: `${Math.min(100, (data.review_score / 10) * 100)}%` }} />
                </div>
                {data.deep_link && (
                  <a href={data.deep_link} target="_blank" rel="noopener noreferrer nofollow" onClick={book}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-accent font-semibold hover:underline" data-testid="hotel-read-reviews">
                    Read verified guest reviews with our partner <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ) : null}
            {data.overview ? <div><h2 className="text-lg font-semibold mb-1">About this stay</h2><p className="text-sm text-muted-foreground leading-relaxed" data-testid="hotel-detail-overview">{data.overview}</p></div> : null}
            {(data.lat != null && data.lng != null) && (
              <div><h2 className="text-lg font-semibold mb-2">Location</h2>
                <HotelMap offers={[data]} currency={currency} onBook={book} /></div>
            )}
          </div>

          {/* Booking box */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-border bg-card p-4 sticky top-24" data-testid="hotel-detail-book-box">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px] uppercase text-muted-foreground">Check-in</Label><Input type="date" value={checkin} onChange={(e) => setCheckin(e.target.value)} className="h-10" data-testid="hotel-detail-checkin" /></div>
                <div><Label className="text-[11px] uppercase text-muted-foreground">Check-out</Label><Input type="date" value={checkout} onChange={(e) => setCheckout(e.target.value)} className="h-10" data-testid="hotel-detail-checkout" /></div>
              </div>
              <div className="mt-2"><Label className="text-[11px] uppercase text-muted-foreground">Guests</Label><Input type="number" min={1} value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="h-10" data-testid="hotel-detail-guests" /></div>
              <p className="text-xs text-muted-foreground mt-2">{fmtDateISO(checkin)} → {fmtDateISO(checkout)} · {nights} night{nights > 1 ? 's' : ''}</p>
              <div className="mt-3 pt-3 border-t border-border">
                {data.price_inr ? (
                  <><p className="text-2xl font-semibold tabular">{formatPrice(data.price_inr, currency)}</p><p className="text-[11px] text-muted-foreground">total · {nights} night{nights > 1 ? 's' : ''}</p></>
                ) : (
                  <p className="text-base font-semibold text-accent">Check latest price with our partner</p>
                )}
              </div>
              <Button onClick={book} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold mt-3" data-testid="hotel-detail-book">
                {data.price_inr ? 'BOOK NOW' : 'CHECK PRICE'} <ExternalLink className="h-4 w-4 ml-1.5" />
              </Button>
              <p className="text-[11px] text-muted-foreground mt-2 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> You complete your booking securely with our trusted partner.</p>
            </div>
          </div>
        </div>

        {similar.length > 0 && (
          <div className="mt-10" data-testid="hotel-similar-section">
            <h2 className="text-lg font-semibold">Nearby stays within your budget</h2>
            <p className="text-sm text-muted-foreground mb-3">Similarly-priced hotels close to {data.name} in {data.city}.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {similar.map((o) => (
                <button key={o.offer_id} onClick={() => navigate(`/hotels/${o.hotel_id}?ci=${checkin}&co=${checkout}&g=${guests}`)}
                  className="text-left rounded-2xl border border-border bg-card overflow-hidden card-elevate" data-testid={`similar-hotel-${o.hotel_id}`}>
                  <div className="h-32 relative">
                    <img src={o.image} alt={o.name} loading="lazy" className="w-full h-full object-cover" />
                    {o.distance_km != null && <span className="absolute top-2 right-2 text-[10px] font-semibold bg-primary/85 text-primary-foreground px-1.5 py-0.5 rounded">{o.distance_km} km away</span>}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-0.5">{Array.from({ length: o.stars || 0 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}</div>
                    <p className="font-semibold text-sm truncate">{o.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm font-semibold tabular">{formatPrice(o.price_inr, currency)}</span>
                      {o.review_score ? <span className="text-[11px] rounded bg-accent/12 text-accent px-1.5 py-0.5 font-semibold">{o.review_score}</span> : null}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
